import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { useCallback, useContext, useId, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'

import { CellNavigationContext } from '../../contexts/CellNavigationContext.js'
import { PortalContainerContext } from '../../contexts/PortalContainerContext.js'
import type { CustomMenuGroup } from '../../helpers/columnConfiguration.js'
import type { Direction } from '../../helpers/sort'
import { useFocusManagement } from '../../hooks/useFocusManagement.js'

function getSortDirection(direction?: Direction) {
  switch (direction) {
    case 'ascending':
      return 'Ascending'
    case 'descending':
      return 'Descending'
    case undefined:
      return 'No sort'
  }
}

interface MenuGroupProps {
  title: string
  children: ReactNode
}

function MenuGroup({ title, children }: MenuGroupProps) {
  return (
    <>
      <div role="separator">{title}</div>
      {children}
    </>
  )
}

interface MenuItemProps {
  onClick?: () => void
  label: string
}

function MenuItem({ onClick, label }: MenuItemProps) {
  const handleClick = useMemo(() => {
    if (!onClick) {
      return undefined
    }
    return (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onClick()
    }
  }, [onClick])

  const handleKeyDown = useMemo(() => {
    if (!onClick) {
      return undefined
    }
    return (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.stopPropagation()
        e.preventDefault()
        onClick()
      }
    }
  }, [onClick])

  // TODO(SL): if onClick is undefined, this item should be disabled. We currently only set 'aria-disabled', which is a semantic indication.
  // We would need to add some styles for that, and also handle the keyboard events to prevent interaction, but ensure the focus navigation still works.
  return (
    <button
      role="menuitem"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      type="button"
      aria-disabled={onClick === undefined}
    >
      {label}
    </button>
  )
}

interface OverlayProps {
  onClick: (e: MouseEvent<HTMLDivElement>) => void
}

function Overlay({ onClick }: OverlayProps) {
  return <div role="presentation" onClick={onClick} />
}

interface ColumnMenuProps {
  columnName: string
  isOpen: boolean
  position: {
    left: number
    top: number
  }
  direction?: Direction
  sortable?: boolean
  toggleOrderBy?: () => void
  hideColumn?: () => void // returns a function to hide the column, or undefined if the column cannot be hidden
  showAllColumns?: () => void // returns a function to show all columns, or undefined
  close: () => void
  id?: string
  menuGroups?: CustomMenuGroup[]
}

export default function ColumnMenu({
  columnName,
  isOpen,
  position,
  direction,
  sortable,
  toggleOrderBy,
  hideColumn,
  showAllColumns,
  close,
  id,
  menuGroups,
}: ColumnMenuProps) {
  const container = useContext(PortalContainerContext)

  const { top, left } = position
  const menuRef = useRef<HTMLDivElement | null>(null)
  const labelId = useId()

  const { navigateFocus } = useFocusManagement(isOpen, menuRef)
  const { goToFirstCell } = useContext(CellNavigationContext)

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    switch (e.key) {
      case 'Escape':
        close()
        break
      case 'Enter':
      case ' ':
        // Handled by the menu item
        break
      case 'ArrowUp':
      case 'ArrowLeft':
        navigateFocus('previous')
        break
      case 'ArrowDown':
      case 'ArrowRight':
        navigateFocus('next')
        break
      case 'Home':
        navigateFocus('first')
        break
      case 'End':
        navigateFocus('last')
        break
      case 'Tab':
        navigateFocus(e.shiftKey ? 'previous' : 'next')
        break
    }
  }, [navigateFocus, close])

  const handleOverlayClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      close()
    },
    [close]
  )

  const onWrapperClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
    // Prevent click on the presentation div from propagating
    // Note that the div will receive focus anyway
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const hideColumnAndClose = useMemo(() => {
    if (!hideColumn) {
      return undefined
    }
    return () => {
      hideColumn()

      // The header cell was focused when opening the menu.
      // Move the focus to the left cell to keep ability to navigate with keyboard.
      // TODO(SL): if this function is undefined (meaning the cell navigation is disabled),
      // the focus is lost. We could instead focus the table?
      goToFirstCell?.()

      close()
    }
  }, [hideColumn, close, goToFirstCell])
  const showAllColumnsAndClose = useMemo(() => {
    if (!showAllColumns) {
      return undefined
    }
    return () => {
      showAllColumns()
      // no need to handle the focus here, since the column will still exist.
      close()
    }
  }, [showAllColumns, close])

  if (!isOpen) {
    return null
  }

  const sortDirection = getSortDirection(direction)

  const showVisibilityGroup = !(!hideColumnAndClose && !showAllColumnsAndClose)

  if (!container) {
    return null
  }

  return createPortal(
    <>
      <Overlay onClick={handleOverlayClick} />
      <div
        id={id}
        role="menu"
        style={{ top, left }}
        ref={menuRef}
        tabIndex={-1}
        aria-labelledby={labelId}
        aria-orientation="vertical"
        onKeyDown={handleKeyDown}
        onClick={onWrapperClick}
      >
        <div role="presentation" id={labelId} aria-hidden="true">{columnName}</div>
        {sortable
          && (
            <MenuGroup title="Sort order">
              <MenuItem
                onClick={toggleOrderBy}
                label={sortDirection}
              />
            </MenuGroup>
          )}
        {showVisibilityGroup
          && (
            <MenuGroup title="Visibility">
              {hideColumnAndClose
                && (
                  <MenuItem
                    onClick={hideColumnAndClose}
                    label="Hide column"
                  />
                )}
              {showAllColumnsAndClose
                && (
                  <MenuItem
                    onClick={showAllColumnsAndClose}
                    label="Show all columns"
                  />
                )}
            </MenuGroup>
          )}
        {menuGroups?.map(group => (
          <MenuGroup key={group.title} title={group.title}>
            {group.items.map(item => (
              <MenuItem
                key={item.label}
                onClick={() => {
                  item.onClick(columnName)
                  close()
                }}
                label={item.label}
              />
            ))}
          </MenuGroup>
        ))}
      </div>
    </>,
    container
  )
}
